import { api } from "@/services/api"

import { ApiConversation, ApiMessage, ConversationStatus } from "../hooks/use-chat-store"
import { MessageContent } from "../types"

/**
 * Service for managing channel conversations with the API
 */
export const channelConversationService = {
  /**
   * Fetches conversations from the API
   * @param status - Filter by conversation status (active or closed)
   * @returns Array of conversations
   */
  getConversations: async (
    status: ConversationStatus = "all",
    limit: number = 20,
    offset: number = 0,
    search?: string,
    tagsInclude?: string[],
    tagsExclude?: string[],
    isGroup?: boolean,
    channelIds?: string[],
    attendantIds?: string[],
    includeUnassigned?: boolean,
    assignedToMe?: boolean
  ) => {
    const response = await api.get<ApiConversation[]>("/communication/conversations", {
      params: {
        ...(status !== "all" ? { status } : {}),
        ...(search ? { search } : {}),
        ...(tagsInclude?.length ? { tags_include: tagsInclude } : {}),
        ...(tagsExclude?.length ? { tags_exclude: tagsExclude } : {}),
        ...(isGroup !== undefined ? { is_group: isGroup } : {}),
        ...(channelIds?.length ? { channel_ids: channelIds } : {}),
        ...(attendantIds?.length ? { attendant_ids: attendantIds } : {}),
        ...(includeUnassigned ? { include_unassigned: true } : {}),
        ...(assignedToMe ? { assigned_to_me: true } : {}),
        limit,
        offset,
      },
    })
    return response.data
  },

  /**
   * Fetches per-status conversation counts for the inbox tab badges.
   *
   * Counts are computed server-side, so they reflect real totals instead of
   * the size of a single paginated page. Accepts the same filters as
   * getConversations so the badges always agree with the visible list.
   * @returns Object with waiting/active/closed counts and their total
   */
  getConversationCounts: async (filters?: {
    channelIds?: string[]
    tagsInclude?: string[]
    tagsExclude?: string[]
    isGroup?: boolean
    attendantIds?: string[]
    includeUnassigned?: boolean
    assignedToMe?: boolean
  }) => {
    const response = await api.get<{
      waiting: number
      active: number
      closed: number
      all: number
      // Conversations with unread inbound messages, per status.
      unread?: {
        waiting: number
        active: number
        closed: number
        all: number
      }
    }>("/communication/conversations/counts", {
      params: {
        ...(filters?.channelIds?.length ? { channel_ids: filters.channelIds } : {}),
        ...(filters?.tagsInclude?.length ? { tags_include: filters.tagsInclude } : {}),
        ...(filters?.tagsExclude?.length ? { tags_exclude: filters.tagsExclude } : {}),
        ...(filters?.isGroup !== undefined ? { is_group: filters.isGroup } : {}),
        ...(filters?.attendantIds?.length ? { attendant_ids: filters.attendantIds } : {}),
        ...(filters?.includeUnassigned ? { include_unassigned: true } : {}),
        ...(filters?.assignedToMe ? { assigned_to_me: true } : {}),
      },
    })
    return response.data
  },

  getConversation: async (conversationId: string) => {
    const response = await api.get<ApiConversation>(
      `/communication/conversations/${conversationId}`
    )
    return response.data
  },

  assignAttendant: async (conversationId: string, attendantId: string | null) => {
    const response = await api.patch<ApiConversation>(
      `/communication/conversations/${conversationId}/assign`,
      { attendant_id: attendantId }
    )
    return response.data
  },

  /**
   * Fetches messages for a specific conversation
   * @param conversationId - The ID of the conversation
   * @returns Array of messages in the conversation
   */
  getMessages: async (conversationId: string, limit?: number, beforeId?: string) => {
    const response = await api.get<ApiMessage[]>(
      `/communication/conversations/${conversationId}/messages`,
      {
        params: {
          ...(limit ? { limit } : {}),
          ...(beforeId ? { before_id: beforeId } : {}),
        },
      }
    )
    return response.data
  },

  /**
   * Sends a message in a conversation
   * @param conversationId - The ID of the conversation
   * @param content - The message content
   * @param options - Additional message options
   * @returns The sent message data
   */
  sendMessage: async (
    conversationId: string,
    content: MessageContent,
    options: { replyTo?: string; forwarded?: boolean; forwardedFrom?: string } = {}
  ) => {
    const response = await api.post(`/communication/messages`, {
      conversation_id: conversationId,
      message_type: content.type,
      content: content,
      ...(options.replyTo ? { reply_to: options.replyTo } : {}),
    })
    return response.data
  },

  /**
   * Marks all messages in a conversation as read
   * @param conversationId - The ID of the conversation to mark as read
   */
  markAsRead: async (conversationId: string) => {
    await api.post(`/communication/conversations/${conversationId}/mark-read`)
  },

  /**
   * Updates the status of a conversation
   * @param conversationId - The ID of the conversation
   * @param status - The new status for the conversation
   */
  updateConversationStatus: async (
    conversationId: string,
    status: "active" | "closed" | "waiting"
  ) => {
    const response = await api.patch(`/communication/conversations/${conversationId}/status`, {
      status,
    })
    return response.data
  },

  /**
   * Ends the active handoff for a conversation
   * @param conversationId - The ID of the conversation
   * @returns The updated conversation data
   */
  endHandoff: async (conversationId: string) => {
    const response = await api.post<ApiConversation>(
      `/communication/conversations/${conversationId}/end-handoff`
    )
    return response.data
  },

  bulkCloseConversations: async () => {
    const response = await api.post<{ closed_count: number }>(
      "/communication/conversations/bulk-close"
    )
    return response.data
  },

  forwardMessage: async (sourceMessageId: string, targetConversationId: string, note?: string) => {
    const response = await api.post(`/communication/messages/forward`, {
      source_message_id: sourceMessageId,
      target_conversation_id: targetConversationId,
      ...(note ? { note } : {}),
    })
    return response.data
  },

  getMediaUrl: async (messageId: string): Promise<string> => {
    const response = await api.get<{ url: string }>(`/communication/messages/${messageId}/media`)
    return response.data.url
  },

  sendPixButton: async (
    conversationId: string,
    data: { pixType: string; pixKey: string; pixName?: string }
  ) => {
    const response = await api.post(`/communication/messages/pix-button`, {
      conversation_id: conversationId,
      pix_type: data.pixType,
      pix_key: data.pixKey,
      ...(data.pixName ? { pix_name: data.pixName } : {}),
    })
    return response.data
  },

  sendRequestPayment: async (
    conversationId: string,
    data: {
      amount: number
      pixKey?: string
      pixType?: string
      pixName?: string
      title?: string
      text?: string
      footer?: string
      itemName?: string
      invoiceNumber?: string
      paymentLink?: string
      fileUrl?: string
      fileName?: string
      boletoCode?: string
    }
  ) => {
    const response = await api.post(`/communication/messages/request-payment`, {
      conversation_id: conversationId,
      amount: data.amount,
      ...(data.pixKey ? { pix_key: data.pixKey } : {}),
      ...(data.pixType ? { pix_type: data.pixType } : {}),
      ...(data.pixName ? { pix_name: data.pixName } : {}),
      ...(data.title ? { title: data.title } : {}),
      ...(data.text ? { text: data.text } : {}),
      ...(data.footer ? { footer: data.footer } : {}),
      ...(data.itemName ? { item_name: data.itemName } : {}),
      ...(data.invoiceNumber ? { invoice_number: data.invoiceNumber } : {}),
      ...(data.paymentLink ? { payment_link: data.paymentLink } : {}),
      ...(data.fileUrl ? { file_url: data.fileUrl } : {}),
      ...(data.fileName ? { file_name: data.fileName } : {}),
      ...(data.boletoCode ? { boleto_code: data.boletoCode } : {}),
    })
    return response.data
  },

  startConversation: async (data: {
    channel_id: string
    phone_number: string
    message_content?: string
    handling_mode: string
    contact_name?: string
    template_name?: string
    template_language_code?: string
    template_components?: Record<string, unknown>[]
  }) => {
    const response = await api.post(`/communication/conversations/start`, data)
    return response.data
  },

  requestHistory: async (conversationId: string, count: number = 20) => {
    const response = await api.post(
      `/communication/conversations/${conversationId}/request-history`,
      null,
      { params: { count } }
    )
    return response.data
  },

  getHistorySyncStatus: async (
    conversationId: string
  ): Promise<{ status?: string; request_id?: string }> => {
    const response = await api.get(
      `/communication/conversations/${conversationId}/history-sync-status`
    )
    return response.data
  },

  resetAgentConversation: async (
    conversationId: string
  ): Promise<{ success: boolean; conversation_id: string }> => {
    const response = await api.post(`/communication/conversations/${conversationId}/agent-reset`)
    return response.data
  },

  fullResetAgentConversation: async (
    conversationId: string
  ): Promise<{
    success: boolean
    conversation_id: string
    contact_id?: string | null
    lead_id?: string | null
  }> => {
    const response = await api.post(
      `/communication/conversations/${conversationId}/agent-reset-full`
    )
    return response.data
  },
}
